import React from 'react'
import { InferType } from 'yup'
import { useAtom } from 'jotai'
import { useMutation, useQuery } from '@tanstack/react-query'
import { RequestForm } from './RequestForm'
import { societyRequestSchema } from '../../../../forms/schemas/society_request'
import { useToastNotifications } from '../../../../hooks/useToastNotification'
import { ToastType } from '../../../common/Toast/toast-type'
import { useAppTranslation } from '../../../../hooks/useAppTranslation'
import {
  SocietyNavigationState,
  societyNavigationStateAtom,
} from '../../../../views/society/atom'
import { acceptUsers, denyRequest } from '../../../../api/requests'
import { EUnitRole } from '../../../../interfaces/models/units.interfaces'
import { IRequestModel } from '../../../../interfaces/models/requests.interfaces'
import { useCurrentSociety } from '../../../../hooks/useCurrentSociety'
import { unitQueries } from '../../../../api/units'
import { entranceQueries } from '../../../../api/society-entrances'
import { userQueries } from '../../../../api/users'

enum Action {
  APPROVE = 'approve',
  DENY = 'deny',
}

interface RequestAccordionSectionContentProps {
  request: IRequestModel
  className?: string
}

export const RequestAccordionSectionContent = ({
  request,
  className,
}: RequestAccordionSectionContentProps): React.ReactNode => {
  const { setToastNotification } = useToastNotifications()
  const { translate } = useAppTranslation()
  const [, setSocietyNavigationState] = useAtom(societyNavigationStateAtom)
  const { society, isLoading: isLoadingSociety } = useCurrentSociety()
  const { data: societyUnits, isLoading: isLoadingUnits } = useQuery(
    unitQueries.getUnitsPerSociety(society?._id)
  )
  const { data: societyEntrances, isLoading: isLoadingEntrances } = useQuery(
    entranceQueries.getEntrancesPerSociety(society?._id)
  )
  const { data: user, isLoading: isLoadingUser } = useQuery(
    userQueries.getUser(request.userId)
  )

  const showSuccessMessage = (action: Action): void => {
    setToastNotification(
      ToastType.SUCCESS,
      action === Action.APPROVE
        ? translate('registerRequest.flashMessage.approveRequestSuccess')
        : translate('registerRequest.flashMessage.denyRequestSuccess')
    )
  }

  const showErrorMessage = (action: Action): void => {
    setToastNotification(
      ToastType.DANGER,
      action === Action.APPROVE
        ? translate('registerRequest.flashMessage.approveRequestFailure')
        : translate('registerRequest.flashMessage.denyRequestFailure')
    )
  }

  const onError = (): void => {
    setToastNotification(
      ToastType.DANGER,
      translate('flashMessage.somethingWentWrongError')
    )
  }

  const { mutate: mutateAcceptRquest, isPaused: isAcceptingRequest } =
    useMutation({
      mutationFn: acceptUsers,
      onSuccess: () => {
        showSuccessMessage(Action.APPROVE)
      },
      onError: () => {
        showErrorMessage(Action.APPROVE)
      },
      onSettled: () => {
        setSocietyNavigationState(SocietyNavigationState.None)
      },
    })

  const onApprove = async (
    data: InferType<typeof societyRequestSchema>
  ): Promise<void> => {
    mutateAcceptRquest({
      data: [
        {
          id: request._id,
          unitRole: data.role as EUnitRole,
          entranceId: data.entranceId,
          unitId: data.unitId,
        },
      ],
    })
  }

  const { mutate: mutateDenyRquest, isPending: isDenyingRequest } = useMutation(
    {
      mutationFn: denyRequest,
      onSuccess: () => {
        showSuccessMessage(Action.DENY)
      },
      onError: () => {
        showErrorMessage(Action.DENY)
      },
      onSettled: () => {
        setSocietyNavigationState(SocietyNavigationState.None)
      },
    }
  )

  const onDeny = async (): Promise<void> => {
    mutateDenyRquest(request._id)
  }

  const loading =
    isDenyingRequest ||
    isAcceptingRequest ||
    isLoadingSociety ||
    isLoadingUser ||
    isLoadingUnits ||
    isLoadingEntrances

  const requestUnit = societyUnits?.find((unit) => request.unitId === unit._id)
  return (
    <div className={`mb-4 ${className ?? ''}`}>
      {society && user && requestUnit && (
        <RequestForm
          loading={loading}
          request={request}
          onError={onError}
          onApprove={onApprove}
          onDeny={onDeny}
          society={society}
          societyUnits={societyUnits || []}
          societyEntrances={societyEntrances || []}
          user={user}
          requestUnit={requestUnit}
        />
      )}
    </div>
  )
}
